import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Plus, Trash2, X, GitBranch, Loader2, Users, Edit2, Eye, ChevronDown, ChevronRight
} from 'lucide-react';

const COST_TYPES = ['materials', 'salaries', 'equipment', 'transport', 'fuel', 'general'];
const COST_TYPE_LABEL = {
  materials: 'Materials / مواد',
  salaries: 'Salaries / رواتب',
  equipment: 'Equipment / معدات',
  transport: 'Transport / نقل',
  fuel: 'Fuel / وقود',
  general: 'General / عام',
};
const METHODS = [
  { v: 'direct', l: 'Direct (user picks)' },
  { v: 'user_based', l: 'User-Based (employee → project)' },
  { v: 'gps', l: 'GPS Geofence' },
  { v: 'auto_by_type', l: 'Default by cost type' },
  { v: 'percentage', l: 'Percentage split' },
];

const emptyRule = () => ({
  name: '', cost_type: 'general', allocation_method: 'auto_by_type',
  default_project_id: '', percentage_splits: [], priority: 100, active: true, notes: '',
});
const emptyMapping = () => ({ employee_id: '', project_id: '', active: true });

const CostAllocationRules = ({ user }) => {
  const [tab, setTab] = useState('rules');
  const [rules, setRules] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [ruleForm, setRuleForm] = useState(emptyRule());
  const [mapForm, setMapForm] = useState(emptyMapping());
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewParams, setPreviewParams] = useState({ cost_type: 'fuel', project_id: '', employee_id: '' });

  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, m, p, e] = await Promise.all([
        axiosInstance.get('/cost-allocation/rules'),
        axiosInstance.get('/cost-allocation/employee-mappings'),
        axiosInstance.get('/projects'),
        axiosInstance.get('/employees').catch(() => ({ data: [] })),
      ]);
      setRules(r.data); setMappings(m.data); setProjects(p.data); setEmployees(e.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Rule modal handlers ──────────────────────────────────────────────
  const openCreateRule = () => { setRuleForm(emptyRule()); setEditingRuleId(null); setShowRuleModal(true); };
  const openEditRule = (r) => {
    setRuleForm({
      name: r.name, cost_type: r.cost_type, allocation_method: r.allocation_method,
      default_project_id: r.default_project_id || '',
      percentage_splits: r.percentage_splits || [],
      priority: r.priority, active: r.active, notes: r.notes || '',
    });
    setEditingRuleId(r.id);
    setShowRuleModal(true);
  };

  const updateSplit = (idx, patch) => {
    setRuleForm((prev) => {
      const splits = [...prev.percentage_splits];
      splits[idx] = { ...splits[idx], ...patch };
      return { ...prev, percentage_splits: splits };
    });
  };
  const addSplit = () => setRuleForm((p) => ({ ...p, percentage_splits: [...p.percentage_splits, { project_id: '', percentage: 0 }] }));
  const removeSplit = (i) => setRuleForm((p) => ({ ...p, percentage_splits: p.percentage_splits.filter((_, idx) => idx !== i) }));
  const splitTotal = ruleForm.percentage_splits.reduce((s, x) => s + Number(x.percentage || 0), 0);

  const submitRule = async (e) => {
    e.preventDefault();
    if (!ruleForm.name.trim()) return toast.error('Name required');
    if (ruleForm.allocation_method === 'percentage' && Math.abs(splitTotal - 100) > 0.01)
      return toast.error(`Splits must sum to 100% (currently ${splitTotal})`);
    setSubmitting(true);
    try {
      const payload = {
        ...ruleForm,
        default_project_id: ruleForm.default_project_id || null,
        percentage_splits: ruleForm.percentage_splits.map((s) => ({
          project_id: s.project_id,
          percentage: Number(s.percentage || 0),
        })),
        priority: Number(ruleForm.priority || 100),
      };
      if (editingRuleId) {
        await axiosInstance.put(`/cost-allocation/rules/${editingRuleId}`, payload);
        toast.success('تم التحديث');
      } else {
        await axiosInstance.post('/cost-allocation/rules', payload);
        toast.success('تم إنشاء القاعدة');
      }
      setShowRuleModal(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل');
    } finally { setSubmitting(false); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('حذف القاعدة؟')) return;
    try { await axiosInstance.delete(`/cost-allocation/rules/${id}`); toast.success('تم الحذف'); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'فشل'); }
  };

  // ── Mapping handlers ─────────────────────────────────────────────────
  const submitMapping = async (e) => {
    e.preventDefault();
    if (!mapForm.employee_id || !mapForm.project_id) return toast.error('Both fields required');
    setSubmitting(true);
    try {
      await axiosInstance.post('/cost-allocation/employee-mappings', mapForm);
      toast.success('تم إنشاء الربط');
      setShowMapModal(false);
      setMapForm(emptyMapping());
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'فشل'); }
    finally { setSubmitting(false); }
  };

  const deleteMapping = async (id) => {
    if (!window.confirm('حذف الربط؟')) return;
    try { await axiosInstance.delete(`/cost-allocation/employee-mappings/${id}`); toast.success('تم الحذف'); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'فشل'); }
  };

  // ── Preview Engine ───────────────────────────────────────────────────
  const runPreview = async () => {
    try {
      const params = new URLSearchParams();
      if (previewParams.cost_type) params.append('cost_type', previewParams.cost_type);
      if (previewParams.project_id) params.append('project_id', previewParams.project_id);
      if (previewParams.employee_id) params.append('employee_id', previewParams.employee_id);
      const res = await axiosInstance.post(`/cost-allocation/preview?${params}`);
      setPreview(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'فشل'); }
  };

  return (
    <div className="space-y-6" data-testid="cost-allocation-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <GitBranch className="w-7 h-7 text-indigo-600" /> Cost Allocation Engine
          </h1>
          <p className="text-sm text-gray-500 mt-1">قواعد التوزيع الذكي — كل مصروف يُوجَّه تلقائياً للمشروع الصحيح</p>
        </div>
        <div className="flex gap-2">
          {tab === 'rules' && isAdmin && (
            <button data-testid="new-rule-btn" onClick={openCreateRule}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Rule
            </button>
          )}
          {tab === 'mappings' && isAdmin && (
            <button data-testid="new-map-btn" onClick={() => { setMapForm(emptyMapping()); setShowMapModal(true); }}
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 flex items-center gap-2">
              <Plus className="w-4 h-4" /> Map Employee
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {[
          { k: 'rules', label: 'Rules', icon: GitBranch },
          { k: 'mappings', label: 'Employee → Project', icon: Users },
          { k: 'preview', label: 'Preview Engine', icon: Eye },
        ].map(({ k, label, icon: Icon }) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px ${
              tab === k ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="bg-white rounded-lg border p-10 text-center text-gray-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
      ) : tab === 'rules' ? (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {rules.length === 0 ? <div className="p-10 text-center text-gray-400">لا توجد قواعد. أنشئ القاعدة الأولى.</div> : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Priority</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Name</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Cost Type</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Method</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Default / Splits</th>
                  <th className="px-3 py-2 text-center text-[11px] uppercase text-gray-500 font-semibold">Active</th>
                  <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm font-mono">{r.priority}</td>
                    <td className="px-3 py-2 text-sm font-medium">{r.name}</td>
                    <td className="px-3 py-2 text-sm">{COST_TYPE_LABEL[r.cost_type] || r.cost_type}</td>
                    <td className="px-3 py-2 text-sm text-gray-700">{r.allocation_method}</td>
                    <td className="px-3 py-2 text-sm">
                      {r.allocation_method === 'percentage'
                        ? <div className="flex flex-wrap gap-1">{r.percentage_splits?.map((s, i) => <span key={i} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded">{s.percentage}% → {s.project_name}</span>)}</div>
                        : <span className="text-gray-700">{r.default_project_name || '—'}</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded ${r.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{r.active ? 'Yes' : 'No'}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isAdmin && (
                        <div className="flex justify-end gap-1">
                          <button data-testid={`edit-rule-${r.id}`} onClick={() => openEditRule(r)} className="p-1.5 hover:bg-blue-50 rounded text-blue-600"><Edit2 className="w-4 h-4" /></button>
                          <button data-testid={`delete-rule-${r.id}`} onClick={() => deleteRule(r.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : tab === 'mappings' ? (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {mappings.length === 0 ? <div className="p-10 text-center text-gray-400">لا توجد ارتباطات. اربط موظفاً بمشروع.</div> : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Employee</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Project</th>
                  <th className="px-3 py-2 text-center text-[11px] uppercase text-gray-500 font-semibold">Active</th>
                  <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((m) => (
                  <tr key={m.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm font-medium">{m.employee_name}</td>
                    <td className="px-3 py-2 text-sm">{m.project_name}</td>
                    <td className="px-3 py-2 text-center"><span className={`text-xs px-2 py-0.5 rounded ${m.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{m.active ? 'Yes' : 'No'}</span></td>
                    <td className="px-3 py-2 text-right">
                      {isAdmin && (
                        <button data-testid={`delete-map-${m.id}`} onClick={() => deleteMapping(m.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        // PREVIEW TAB
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="text-sm font-semibold text-gray-700">Simulate an expense and see how the engine allocates it:</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Cost Type</label>
                <select value={previewParams.cost_type} onChange={(e) => setPreviewParams({ ...previewParams, cost_type: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                  {COST_TYPES.map((c) => <option key={c} value={c}>{COST_TYPE_LABEL[c]}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Explicit Project (optional)</label>
                <select value={previewParams.project_id} onChange={(e) => setPreviewParams({ ...previewParams, project_id: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                  <option value="">— None —</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Employee (optional)</label>
                <select value={previewParams.employee_id} onChange={(e) => setPreviewParams({ ...previewParams, employee_id: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                  <option value="">— None —</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name || e.name_english || e.name_arabic}</option>)}
                </select>
              </div>
            </div>
            <button data-testid="run-preview-btn" onClick={runPreview}
              className="px-4 py-1.5 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700">Run Engine</button>
          </div>
          {preview && (
            <div data-testid="preview-result" className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
              <div className="text-xs text-gray-500 mb-2">Decision:</div>
              <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                <div><span className="text-gray-500">Method:</span> <strong className="text-indigo-700">{preview.method}</strong></div>
                <div><span className="text-gray-500">Matched Rule:</span> <strong>{preview.matched_rule_name || '— none —'}</strong></div>
                <div className="col-span-2"><span className="text-gray-500">Reason:</span> <em>{preview.reason}</em></div>
              </div>
              {preview.splits?.length > 0 ? (
                <div className="space-y-1">
                  {preview.splits.map((s, i) => (
                    <div key={i} className="flex items-center justify-between bg-white rounded px-3 py-1.5 border border-indigo-100">
                      <span className="text-sm font-medium">{s.project_name || s.project_id}</span>
                      <span className="font-mono text-emerald-700 font-semibold">{s.percentage}%</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded">No project assigned — expense will be unallocated.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Rule Modal */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowRuleModal(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto" data-testid="rule-modal">
            <div className="px-5 py-3 border-b flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-semibold">{editingRuleId ? 'Edit' : 'New'} Allocation Rule</h3>
              <button onClick={() => setShowRuleModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitRule} className="px-5 py-4 space-y-3">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Rule Name *</label>
                <input data-testid="rule-name" required value={ruleForm.name} onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Cost Type</label>
                  <select value={ruleForm.cost_type} onChange={(e) => setRuleForm({ ...ruleForm, cost_type: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                    {COST_TYPES.map((c) => <option key={c} value={c}>{COST_TYPE_LABEL[c]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Method</label>
                  <select data-testid="rule-method" value={ruleForm.allocation_method} onChange={(e) => setRuleForm({ ...ruleForm, allocation_method: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                    {METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Priority (lower=higher)</label>
                  <input type="number" value={ruleForm.priority} onChange={(e) => setRuleForm({ ...ruleForm, priority: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono" />
                </div>
              </div>
              {ruleForm.allocation_method !== 'percentage' && (
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Default Project</label>
                  <select value={ruleForm.default_project_id} onChange={(e) => setRuleForm({ ...ruleForm, default_project_id: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                    <option value="">— None (use last-active fallback) —</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
              {ruleForm.allocation_method === 'percentage' && (
                <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                  <div className="text-xs font-semibold text-gray-700 flex items-center justify-between">
                    <span>Percentage Splits</span>
                    <button type="button" onClick={addSplit} className="text-emerald-600 text-xs flex items-center gap-1"><Plus className="w-3 h-3" />Add</button>
                  </div>
                  {ruleForm.percentage_splits.map((s, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <select value={s.project_id} onChange={(e) => updateSplit(idx, { project_id: e.target.value })}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs">
                        <option value="">— Select project —</option>
                        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                      <input type="number" step="0.01" value={s.percentage} onChange={(e) => updateSplit(idx, { percentage: e.target.value })}
                        className="w-20 border border-gray-300 rounded px-2 py-1 text-xs text-right font-mono" />
                      <span className="text-xs text-gray-500">%</span>
                      <button type="button" onClick={() => removeSplit(idx)} className="p-1 text-red-500 hover:text-red-700"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <div className={`text-xs text-right font-mono ${Math.abs(splitTotal - 100) < 0.01 ? 'text-emerald-700' : 'text-amber-600'}`}>
                    Total: {splitTotal.toFixed(2)}% {Math.abs(splitTotal - 100) < 0.01 ? '✓' : '(must be 100)'}
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Notes</label>
                <textarea rows={2} value={ruleForm.notes} onChange={(e) => setRuleForm({ ...ruleForm, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={ruleForm.active} onChange={(e) => setRuleForm({ ...ruleForm, active: e.target.checked })} />
                Active
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowRuleModal(false)} className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50">Cancel</button>
                <button type="submit" data-testid="rule-submit" disabled={submitting}
                  className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
                  {submitting ? 'Saving…' : (editingRuleId ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mapping Modal */}
      {showMapModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowMapModal(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl max-w-md w-full" data-testid="map-modal">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold">Map Employee → Project</h3>
              <button onClick={() => setShowMapModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitMapping} className="px-5 py-4 space-y-3">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Employee *</label>
                <select data-testid="map-employee" required value={mapForm.employee_id} onChange={(e) => setMapForm({ ...mapForm, employee_id: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                  <option value="">— Select —</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name || e.name_english || e.name_arabic}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Project *</label>
                <select data-testid="map-project" required value={mapForm.project_id} onChange={(e) => setMapForm({ ...mapForm, project_id: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                  <option value="">— Select —</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowMapModal(false)} className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50">Cancel</button>
                <button type="submit" data-testid="map-submit" disabled={submitting}
                  className="px-4 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                  {submitting ? 'Saving…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostAllocationRules;
